import { useCallback, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { makeStyles, mergeClasses, tokens } from '@coreai/fluentui-react';
import { Home20Filled, Home20Regular } from '@fluentui/react-icons';
import { Sidebar, type SidebarSection } from '@/components/Sidebar/Sidebar';
import { useTelemetry } from '@/hooks/useTelemetry';
import { tenantPrefix } from '@/routes';
import { useAppShellStyles } from './AppShell.styles';
import { APP_SHELL_MOBILE_QUERY } from '@/theme/appShell';

const useStyles = makeStyles({
  mainContent: {
    overflow: 'auto',
    paddingTop: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXXXL,
    [`@media ${APP_SHELL_MOBILE_QUERY}`]: {
      paddingTop: tokens.spacingVerticalS,
      paddingRight: tokens.spacingHorizontalM,
      paddingBottom: tokens.spacingVerticalL,
      paddingLeft: tokens.spacingHorizontalM,
    },
  },
});

export const HomeLayout = () => {
  const styles = useStyles();
  const shellStyles = useAppShellStyles();
  const { trackEvent } = useTelemetry();
  const { locationName, tenantId: routeTenantId } = useParams<{ locationName?: string; tenantId?: string }>();

  const sections = useMemo<SidebarSection[]>(() => {
    // Use the route tenant param (not the store) to build the Home path so
    // NavLink matches the current URL. At the bare `/` route neither
    // routeTenantId nor locationName exist, and the path should be `/`.
    const prefix = tenantPrefix(routeTenantId);
    const homePath = locationName ? `${prefix}/location/${encodeURIComponent(locationName)}` : (prefix || '/');
    return [
      {
        id: 'home',
        items: [
          { id: 'home', label: 'Home', icon: Home20Regular, activeIcon: Home20Filled, path: homePath, end: true },
        ],
      },
    ];
  }, [routeTenantId, locationName]);

  const handleSidebarItemClick = useCallback(
    (id: string) => trackEvent('navigation.sidebar.clicked', { destination: id }),
    [trackEvent],
  );

  return (
    <div className={shellStyles.body}>
      <Sidebar
        ariaLabel="Home navigation"
        navAriaLabel="Home"
        sections={sections}
        onItemClick={handleSidebarItemClick}
      />
      <div className={mergeClasses(shellStyles.main, styles.mainContent)}>
        <Outlet />
      </div>
    </div>
  );
};
